<template>
  <section
    class="SeasonListItemView"
    :data-testid="`seasonListItem-${season.id}`"
  >
    <SeasonHero
      v-bind="{ season }"
      class="SeasonListItemView__hero"
    />
    <ol
      v-if="sortedRuns.length > 0"
      class="SeasonListItemView__runGrid"
      aria-label="Runs in season"
    >
      <li
        v-for="run in sortedRuns"
        :key="run.id"
      >
        <SeasonRunButton
          v-bind="{ run }"
          @click="handleRunClick(run)"
        />
      </li>
    </ol>
    <div
      v-if="isCommishForSeason"
      class="SeasonListItemView__buttonContainer"
      data-theme="light"
    >
      <SeasonScheduleRunButtonView
        v-bind="{ season }"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import SeasonHero from "@/components/SeasonHero.vue";
import SeasonRunButton from "@/components/SeasonRunButton.vue";
import getRouteForRun from "@/lib/utils/route/getRouteForRun.ts";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore.ts";
import useModalStore from "@/stores/useModalStore.ts";
import { Run, SeasonWithMaybeCourt, SeasonWithMaybeCrew, SeasonWithRuns } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import SeasonScheduleRunButtonView from "./SeasonScheduleRunButtonView.vue";

const {
  season,
} = defineProps<{
  season: SeasonWithMaybeCrew & SeasonWithMaybeCourt & SeasonWithRuns,
}>();

const {
  crewMemberships,
} = storeToRefs(useCurrentPlayerStore());

const {
  openModal,
} = useModalStore();

const isCommishForSeason = computed(() => {
  return crewMemberships.value.some((membership) => membership.crew_id === season.crew_id && membership.role === "commissioner");
});

const sortedRuns = computed(() => {
  return [...season.runs].sort((a, b) => {
    return +new Date(a.start_at) - +new Date(b.start_at);
  });
});

function handleRunClick (run: Run) {
  openModal(getRouteForRun(run.id));
}
</script>

<style lang="scss" scoped>
.SeasonListItemView {
  display: grid;
  grid-template-rows: min-content auto;
  overflow: hidden;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);
}

.SeasonListItemView__hero {
  --titleSize: 28px;
  --dateRangeSize: 20px;
  --courtIconSize: 14px;
  --courtFontSize: 16px;
}

.SeasonListItemView__runGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 12px;
  padding: 16px;

  border-bottom: solid 1px var(--themeColorNeutral100);
}

.SeasonListItemView__buttonContainer {
  padding: 16px;
}
</style>
